import React, { useEffect, useState } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Loader2, Wifi, WifiOff, Server, Shield, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Clock, Globe, ArrowUpRight, ChevronDown, ChevronUp,
  Landmark, Activity, Database, Terminal
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
  connected: { label: 'Connected', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle, dot: 'bg-emerald-400' },
  ip_blocked: { label: 'IP Not Whitelisted', color: 'text-amber-600', bg: 'bg-amber-50', icon: AlertTriangle, dot: 'bg-amber-400' },
  unreachable: { label: 'Unreachable', color: 'text-red-600', bg: 'bg-red-50', icon: XCircle, dot: 'bg-red-400' },
  error: { label: 'Error', color: 'text-red-600', bg: 'bg-red-50', icon: XCircle, dot: 'bg-red-400' },
  not_configured: { label: 'Not Configured', color: 'text-gray-500', bg: 'bg-gray-50', icon: XCircle, dot: 'bg-gray-400' },
};

export default function BankingPartners() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedCall, setExpandedCall] = useState(null);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [result, cs] = await Promise.all([
        superAdminAPI.getBankingPartners(),
        superAdminAPI.getConfigStatus().catch(() => null),
      ]);
      setData(result);
      setConfigStatus(cs);
      if (isRefresh) toast.success('Diagnostics refreshed');
    } catch (err) {
      toast.error('Failed to load banking partners');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;
  if (!data) return null;

  const { partners, api_calls, server_ip, recent_logs, checked_at } = data;

  return (
    <div className="space-y-6" data-testid="banking-partners-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Banking Partners</h1>
          <p className="text-sm text-gray-500">Monitor connections, diagnose issues, and view API call logs</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing}
          className="h-8 text-xs border-gray-200" data-testid="refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Checking...' : 'Run Diagnostics'}
        </Button>
      </div>

      {/* Server Info Bar */}
      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-3 flex flex-wrap items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <Server className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-gray-500">Server IP:</span>
            <span className="font-mono font-semibold text-gray-900" data-testid="server-ip">{server_ip}</span>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-500">Last check:</span>
            <span className="text-gray-900">{checked_at ? new Date(checked_at).toLocaleString() : 'Never'}</span>
          </div>
        </CardContent>
      </Card>

      {/* Diagnostic Config Info */}
      {configStatus && (
        <Card className="border-blue-200 bg-blue-50/30 shadow-sm" data-testid="diagnostic-config-info">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase font-semibold text-blue-500 tracking-wider mb-2">DIAGNOSTIC CONFIGURATION</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <p className="text-[10px] text-gray-500 font-medium">Current URL</p>
                <p className="text-xs font-mono font-semibold text-gray-900 break-all" data-testid="diag-active-url">{configStatus.active_url}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-medium">URL Source</p>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded inline-block mt-0.5 ${configStatus.url_source === 'DATABASE' ? 'bg-blue-100 text-blue-700' : configStatus.url_source === 'ENV_VAR' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}
                  data-testid="diag-url-source">
                  {configStatus.url_source}
                </span>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-medium">Environment</p>
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded inline-block mt-0.5 ${configStatus.environment === 'PRODUCTION' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                  data-testid="diag-environment">
                  {configStatus.environment}
                </span>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-medium">Outbound IP</p>
                <p className="text-xs font-mono font-semibold text-gray-900" data-testid="diag-outbound-ip">{configStatus.outbound_ip}</p>
                {configStatus.ip_matches_fixie ? (
                  <span className="text-[10px] text-emerald-600 font-medium">Matches Fixie ({configStatus.fixie_environment})</span>
                ) : (
                  <span className="text-[10px] text-red-600 font-medium">Not a known Fixie IP</span>
                )}
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-medium">Proxy</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <div className={`w-2 h-2 rounded-full ${configStatus.proxy_active ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <span className="text-xs font-semibold text-gray-900">{configStatus.proxy_active ? 'Active' : 'Inactive'}</span>
                </div>
                {configStatus.proxy_url && <p className="text-[10px] text-gray-500 font-mono">{configStatus.proxy_url}</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Partner Cards */}
      <div className="space-y-4">
        {partners.map(p => {
          const s = STATUS_MAP[p.status] || STATUS_MAP.error;
          const StatusIcon = s.icon;
          return (
            <Card key={p.id} className="border-gray-200 shadow-sm" data-testid={`partner-${p.id}`}>
              <CardContent className="p-5">
                {/* Partner Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl ${s.bg} flex items-center justify-center shrink-0`}>
                    <Landmark className={`w-6 h-6 ${s.color}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-base font-bold text-gray-900">{p.name}</h2>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${s.dot} ${p.status === 'connected' ? 'animate-pulse' : ''}`} />
                        <span className={`text-xs font-semibold ${s.color}`} data-testid={`status-${p.id}`}>{s.label}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">{p.type}</p>
                  </div>
                </div>

                {/* Partner Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Base URL</p>
                    <p className="text-xs font-mono text-gray-900 truncate">{p.base_url}</p>
                    {p.url_source && <p className="text-[10px] text-blue-600 mt-0.5">Source: {p.url_source}</p>}
                    {p.active_environment && (
                      <span className={`text-[10px] font-medium px-1 py-0.5 rounded mt-0.5 inline-block ${p.active_environment === 'PRODUCTION' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {p.active_environment}
                      </span>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Whitelisted IP</p>
                    <p className="text-xs font-mono text-gray-900">{p.whitelisted_ip}</p>
                    {!p.ip_match && <p className="text-[10px] text-amber-600 mt-0.5">Current IP doesn't match</p>}
                    {p.ip_match && <p className="text-[10px] text-emerald-600 mt-0.5">IP matches</p>}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Available Banks</p>
                    <p className="text-xs font-semibold text-gray-900">{p.banks_available || 0}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Balance</p>
                    <p className="text-xs font-semibold text-gray-900">
                      {p.balance !== null && p.balance !== undefined ? `NGN ${Number(p.balance).toLocaleString()}` : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Error Message */}
                {p.error && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-4" data-testid={`error-${p.id}`}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-red-700">Issue Detected</p>
                        <p className="text-xs text-red-600 mt-0.5">{p.error}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* API Call Logs */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Activity className="w-4 h-4 text-pink-500" />
            API Call Log (Last Diagnostic)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-2">
          {api_calls.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No API calls recorded</p>
          ) : (
            api_calls.map((call, i) => {
              const isOk = call.status_code && call.status_code >= 200 && call.status_code < 300 && !call.error;
              const expanded = expandedCall === i;
              return (
                <div key={i} className="border border-gray-100 rounded-lg overflow-hidden" data-testid={`api-call-${i}`}>
                  <button onClick={() => setExpandedCall(expanded ? null : i)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isOk ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      {isOk ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{call.endpoint}</p>
                      <p className="text-[10px] text-gray-500">{call.purpose}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-xs font-mono font-semibold ${isOk ? 'text-emerald-600' : 'text-red-600'}`}>
                        {call.status_code || 'ERR'}
                      </span>
                      <span className="text-[10px] text-gray-400">{call.duration_ms}ms</span>
                      {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                    </div>
                  </button>
                  {expanded && (
                    <div className="border-t border-gray-100 bg-gray-50 p-3">
                      <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Response</p>
                      <pre className="text-[11px] text-gray-800 bg-white rounded-lg p-3 border border-gray-100 overflow-x-auto max-h-60 whitespace-pre-wrap">
                        {call.error ? `ERROR: ${call.error}` : JSON.stringify(call.response, null, 2)}
                      </pre>
                      <p className="text-[10px] text-gray-400 mt-2">Timestamp: {call.timestamp}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Recent System Logs */}
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-500" />
            Recent API Activity Log
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setShowAllLogs(!showAllLogs)} className="h-7 text-xs text-gray-500">
            {showAllLogs ? 'Show Less' : `Show All (${recent_logs?.length || 0})`}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold text-gray-500">Action</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-500">Actor</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-500 hidden md:table-cell">IP</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-500 hidden lg:table-cell">Details</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-500">Status</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-500">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(showAllLogs ? recent_logs : recent_logs?.slice(0, 15))?.map(log => {
                  const statusCode = log.metadata?.status_code;
                  const isOk = statusCode >= 200 && statusCode < 400;
                  return (
                    <tr key={log.id} className="hover:bg-gray-50/50">
                      <td className="py-1.5 px-3 font-mono text-gray-900 max-w-[200px] truncate">{log.action}</td>
                      <td className="py-1.5 px-3 text-gray-600">{log.actor_type}</td>
                      <td className="py-1.5 px-3 text-gray-500 font-mono hidden md:table-cell">{log.ip_address || '\u2014'}</td>
                      <td className="py-1.5 px-3 text-gray-500 hidden lg:table-cell max-w-[240px] truncate">
                        {log.metadata?.device_type && `${log.metadata.device_type} / ${log.metadata.browser}`}
                        {log.metadata?.duration_ms && ` (${log.metadata.duration_ms}ms)`}
                      </td>
                      <td className="py-1.5 px-3">
                        {statusCode ? (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isOk ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                            {statusCode}
                          </span>
                        ) : <span className="text-gray-300">\u2014</span>}
                      </td>
                      <td className="py-1.5 px-3 text-gray-400 whitespace-nowrap">
                        {log.created_at ? new Date(log.created_at).toLocaleTimeString() : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
